/**
 * Vite `base` always ends in `/`. history v4 / hard navigations want the
 * basename without a trailing slash (`""` at root, `"/next"` under `/next/`).
 */
export const getAppBasename = (): string =>
  import.meta.env.BASE_URL.replace(/\/$/, "");

/**
 * Strip a deploy basename from a browser pathname. Pure helper for
 * {@link getAppPathname} and unit tests.
 */
export const stripAppBasename = (
  basename: string,
  pathname: string,
): string => {
  if (!basename) {
    return pathname;
  }
  if (pathname === basename) {
    return "/";
  }
  if (pathname.startsWith(`${basename}/`)) {
    return pathname.slice(basename.length) || "/";
  }
  return pathname;
};

/**
 * Browser pathname with the deploy basename stripped, so checks like
 * `startsWith("/dashboard")` work under `/next` the same as at the root.
 */
export const getAppPathname = (
  pathname: string = window.location.pathname,
): string => stripAppBasename(getAppBasename(), pathname);

/**
 * Pure join used by {@link withAppBasePath}. Exported for unit tests so we do
 * not have to stub `import.meta.env`.
 *
 * - Leaves absolute / protocol-relative / hash URLs alone (OAuth redirects).
 * - No-ops when `basename` is empty (root deploy).
 * - Idempotent if `path` is already under `basename`.
 */
export const joinAppBasePath = (basename: string, path: string): string => {
  if (!path || /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(path)) {
    return path;
  }
  if (!basename) {
    return path;
  }
  if (path === basename || path.startsWith(`${basename}/`)) {
    return path;
  }
  if (path === "/") {
    return `${basename}/`;
  }
  return `${basename}${path.startsWith("/") ? path : `/${path}`}`;
};

/**
 * Prefix an in-app path with the deploy basename so `window.location` /
 * `history.replaceState` hard navigations stay under `/next` when the app is
 * served from a sub-path. Soft react-router navigations already go through the
 * basename-aware history instance in `App.tsx`.
 */
export const withAppBasePath = (path: string): string =>
  joinAppBasePath(getAppBasename(), path);
